import {
	BookOpen,
	BookText,
	Castle,
	Layers,
	Menu,
	ScrollText,
	Shield,
	Users,
	type LucideIcon,
} from 'lucide-react'
import { Link, useLocation, useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser, userHasRole } from '#app/utils/user.ts'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from './ui/sheet'

type Tab = { to: string; label: string; icon: LucideIcon; end?: boolean }

const baseTabs: Tab[] = [
	{ to: '/', label: 'Hub', icon: Castle, end: true },
	{ to: '/character', label: 'Profil', icon: Users },
	{ to: '/group', label: 'Groupe', icon: Layers },
	{ to: '/content', label: 'Bibliothèque', icon: BookText },
]

const projetsTab: Tab = {
	to: '/projects',
	label: 'Projets',
	icon: ScrollText,
	end: true,
}

const moreTabs: Tab[] = [
	{ to: '/competences', label: 'Compétences', icon: BookOpen },
]

const adminTabs: Tab[] = [
	{ to: '/geopolitics', label: 'Géopolitique', icon: Layers },
	{ to: '/admin', label: 'Admin', icon: Shield, end: true },
]

function isTabActive(pathname: string, to: string, end = false): boolean {
	if (to === '/') return pathname === '/'
	if (end) return pathname === to
	return pathname === to || pathname.startsWith(to + '/')
}

const tabClass =
	'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[11px] leading-tight font-medium transition-all active:scale-[0.92]'

export function MobileTabBar() {
	const user = useOptionalUser()
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const { pathname } = useLocation()

	if (!user) return null

	const isAdmin = userHasRole(user, 'admin')
	const isGroupMember = rootData?.isGroupMember ?? false
	const tabs = isGroupMember ? [...baseTabs, projetsTab] : baseTabs
	const sheetTabs = isAdmin ? [...moreTabs, ...adminTabs] : moreTabs
	const moreActive = sheetTabs.some((t) => isTabActive(pathname, t.to, t.end))

	return (
		<nav
			aria-label='Navigation principale'
			className='border-primary/40 bg-primary fixed inset-x-0 bottom-0 z-40 flex border-t pb-[env(safe-area-inset-bottom)] xl:hidden'
		>
			{tabs.map(({ to, label, icon: Icon, end }) => {
				const active = isTabActive(pathname, to, end)
				return (
					<Link
						key={to}
						to={to}
						prefetch='intent'
						aria-current={active ? 'page' : undefined}
						className={cn(
							tabClass,
							active
								? 'text-primary-foreground font-semibold'
								: 'text-primary-foreground/65 hover:text-primary-foreground',
						)}
					>
						<Icon
							className={cn(
								'h-5 w-5 shrink-0 transition-transform',
								active && 'scale-110',
							)}
							aria-hidden='true'
						/>
						<span className='text-center'>{label}</span>
					</Link>
				)
			})}

			<Sheet>
				<SheetTrigger
					className={cn(
						tabClass,
						moreActive
							? 'text-primary-foreground font-semibold'
							: 'text-primary-foreground/65 hover:text-primary-foreground',
					)}
				>
					<Menu className='h-5 w-5 shrink-0' aria-hidden='true' />
					<span className='text-center'>Plus</span>
				</SheetTrigger>
				<SheetContent side='right' className='flex max-w-xs flex-col'>
					<SheetTitle className='px-6 pt-8 pb-4'>Navigation</SheetTitle>
					<SheetNav tabs={moreTabs} pathname={pathname} />
					{isAdmin ? (
						<>
							<p className='text-muted-foreground px-7 pt-6 pb-2 text-[10px] font-bold tracking-[0.16em] uppercase'>
								Administration
							</p>
							<SheetNav tabs={adminTabs} pathname={pathname} />
						</>
					) : null}
				</SheetContent>
			</Sheet>
		</nav>
	)
}

function SheetNav({ tabs, pathname }: { tabs: Tab[]; pathname: string }) {
	return (
		<nav className='flex flex-col gap-1 px-4'>
			{tabs.map(({ to, label, icon: Icon, end }) => (
				<SheetClose asChild key={to}>
					<Link
						to={to}
						prefetch='intent'
						className={cn(
							'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
							isTabActive(pathname, to, end)
								? 'bg-accent text-accent-foreground'
								: 'hover:bg-accent/50',
						)}
					>
						<Icon className='h-4 w-4 shrink-0' aria-hidden='true' />
						{label}
					</Link>
				</SheetClose>
			))}
		</nav>
	)
}
